import { useNavigate } from 'react-router-dom';
import { useToast } from './useToast';
import { acknowledgeLiveChange, answerLiveLessonTimeProposal, answerLiveWeeklyTimeProposal, withdrawLiveWeeklyTimeProposal, liveLessonsErrorMessage, withdrawLiveLessonTimeProposal } from '../services/liveLessons';
import { markBookingsQuiet } from '../utils/oneToast';
import { completeOptimistically, runBehind } from '../services/optimistic';
import { movedPatch } from '../utils/optimisticLessons';
import type { ActionAnswer } from '../components/live/ActionCard';
import type { LiveChange } from '../utils/actionCard';
import type { LiveCallBooking } from '../types/liveCall';

/**
 * What an Action card's answer does (decision 165 as amended, specs/changes.md 2 and 4), for any page that shows
 * one. Confirm, Withdraw and Looks Good are optimistic: the card has flipped already, the call runs behind with
 * the quiet retry (157), and a refusal rejects so the card puts itself back, with the server's words in a toast.
 * Confirm moves the lesson in place (movedPatch); the flipped card replaces 125's "New time set" toast.
 */
export function useChangeAnswer(d: {
  /** The lesson a confirm moves, when this page holds it. */
  bookingOf?: (bookingId: string) => LiveCallBooking | undefined;
  tz: string;
  /** "None of these work": this side's own times for the lesson (row 4, CoachRequestTimesOverlay side "player"). */
  onNoneWork?: (c: LiveChange) => void;
  /** "Suggest a Different Time": the sheet (rows 1 and 2). */
  onSuggestDifferent?: (c: LiveChange) => void;
  onMessage?: (c: LiveChange) => void;
}): (a: ActionAnswer, c: LiveChange) => Promise<unknown> | void {
  const { showError } = useToast();
  const navigate = useNavigate();
  const fail = (line: string) => (err: unknown) => {
    showError(liveLessonsErrorMessage(err, line));
    throw err;
  };

  return (a, c) => {
    const proposalId = c.proposalId;
    switch (a.act) {
      case 'confirm': {
        if (!proposalId || !a.value) return;
        if (c.kind === 'weekly') {
          // One call, no quiet retry: the weekly answer takes no requestId. Accepting holds the month again, and
          // its lessons' own toasts stay quiet (utils/oneToast); the flipped card says it.
          markBookingsQuiet();
          return answerLiveWeeklyTimeProposal({ proposalId, answer: 'accept', slot: c.to.indexOf(a.value) })
            .catch(fail('Could not answer this suggestion. Please try again.'));
        }
        const time = c.to.indexOf(a.value);
        const b = c.bookingId ? d.bookingOf?.(c.bookingId) : undefined;
        const call = (requestId: string) => answerLiveLessonTimeProposal({ proposalId, answer: 'accept', time, requestId });
        if (!b) return runBehind(call).catch(fail('That time has passed. Pick another.'));
        return new Promise((resolve, reject) => {
          completeOptimistically(b.id, movedPatch(b, a.value!, d.tz), call, {
            onDone: resolve,
            onFail: (err) => { showError(liveLessonsErrorMessage(err, 'That time has passed. Pick another.')); reject(err); },
          });
        });
      }
      case 'withdraw':
        if (!proposalId) return;
        // A weekly suggestion's own withdraw (contracts 16, Ryan's answers): a second call answers failed-precondition.
        if (c.kind === 'weekly') {
          return withdrawLiveWeeklyTimeProposal({ proposalId }).catch(fail("Couldn't withdraw these times. Try again."));
        }
        return runBehind((requestId) => withdrawLiveLessonTimeProposal({ proposalId, requestId }))
          .catch(fail("Couldn't withdraw these times. Try again."));
      case 'none_work':
        // This side's own times open at once (row 4); sending them replaces these, and the sender hears it then.
        // Closing the sheet sends nothing: not answering keeps the time (165, 98).
        d.onNoneWork?.(c);
        return;
      case 'suggest_different':
        d.onSuggestDifferent?.(c);
        return;
      case 'message':
        // Row 9's Message Ryan: the page's own thread opener, else Messages.
        if (d.onMessage) d.onMessage(c); else navigate('/inbox');
        return;
      case 'open':
      case 'join':
        if (c.bookingId) navigate(a.act === 'join' ? `/call/${c.bookingId}` : `/live/${c.bookingId}`);
        return;
      case 'looks_good':
        // Rows 3, 5 and 6 (contracts 16): the record is answered; the move stands. Safe to retry.
        return runBehind(() => acknowledgeLiveChange({ changeId: c.id })).catch(fail("Couldn't save that. Try again."));
    }
  };
}
